/**
 * NutritionGoalsModal — set (or edit) a client's daily targets in one click,
 * from anywhere the "לא הוגדרו יעדים" reminder shows (the workspace banner,
 * the pending queue). Writes `patient.nutrition.goals` through the existing
 * updatePatient pass-through; `nutrition.goalsSet` is derived there, which is
 * what clears the reminder everywhere at once.
 */

import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Barbell, ChartDonut, Target, TrendUp } from "@phosphor-icons/react";
import ModalCloseButton from "../../ui/ModalCloseButton";
import MacroIcon from "../../nutrition/MacroIcon";
import GoalSuggestion from "./GoalSuggestion";
import { useUpdatePatientMutation } from "../../../store/services/patientsApi";
import { useGetMeasurementsQuery } from "../../../store/services/measurementsApi";
import {
  selectTherapistId,
  selectTherapistName,
} from "../../../store/slices/authSlice";
import { useToast } from "../../../contexts/ToastContext";
import { hasNutritionGoals } from "../../../firebase";
import {
  ACTIVITY_LEVEL_LABELS,
  DEFAULT_GOAL_TYPE,
  DIET_PLANS,
  GOAL_TYPES,
  calcAgeFromBirthDate,
  calcBmr,
  macrosFromDietPlan,
  suggestDailyGoals,
} from "../../../utils/nutritionCalc";
import "./NutritionGoalsModal.css";
import LoaderLoop from "../../shared/loader/LoaderLoop";

const MACRO_FIELDS = [
  { key: "calories", label: "קלוריות", unit: "קק״ל" },
  { key: "carbohydrates", label: "פחמימות", unit: "ג׳" },
  { key: "protein", label: "חלבון", unit: "ג׳" },
  { key: "fat", label: "שומן", unit: "ג׳" },
];

function NutritionGoalsModal({ clinicId, patient, onClose, onSaved }) {
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const { showSuccess, showError } = useToast();
  const [updatePatient, { isLoading }] = useUpdatePatientMutation();

  const [goals, setGoals] = useState(() => ({
    dietPlan: "",
    calories: null,
    protein: null,
    carbohydrates: null,
    fat: null,
    ...(patient?.nutrition?.goals || {}),
  }));

  // Activity level + goal type live next to the goals on patient.nutrition;
  // they feed the suggestions, so they are editable here as well.
  const [activityLevel, setActivityLevel] = useState(patient?.nutrition?.activityLevel || "");
  const [goalType, setGoalType] = useState(patient?.nutrition?.goalType || DEFAULT_GOAL_TYPE);

  const setGoal = (key, value) => setGoals((prev) => ({ ...prev, [key]: value }));

  // Suggestions need the latest measurement (weight, effective BMR).
  const { data: measurements = [] } = useGetMeasurementsQuery(
    { clinicId, patientId: patient?.id },
    { skip: !clinicId || !patient?.id }
  );
  const suggestions = useMemo(() => {
    const latest = measurements[0] || null;
    const weight = Number(latest?.weight) || null;
    const bmr =
      latest?.bmr ??
      calcBmr({
        weight,
        height: Number(latest?.height) || null,
        age: calcAgeFromBirthDate(patient?.birthDate),
        gender: patient?.gender,
      });
    return suggestDailyGoals({
      bmr,
      activityLevel,
      weight,
      goalType,
      dietPlan: goals.dietPlan,
      caloriesEntered: goals.calories,
    });
  }, [measurements, patient, activityLevel, goalType, goals.dietPlan, goals.calories]);

  const applyDietPlan = (planKey) => {
    const macros = macrosFromDietPlan(Number(goals.calories) || 0, planKey);
    setGoals((prev) => ({ ...prev, dietPlan: planKey, ...(macros || {}) }));
  };

  const canSave = hasNutritionGoals(goals);

  const handleSave = async () => {
    if (!canSave || !patient?.id) return;
    try {
      await updatePatient({
        clinicId,
        patientId: patient.id,
        patientData: {
          nutrition: {
            ...(patient.nutrition || {}),
            activityLevel: activityLevel || null,
            goalType,
            goals,
          },
        },
        therapistId,
        therapistName,
      }).unwrap();
      showSuccess("היעדים היומיים נשמרו");
      if (onSaved) onSaved(goals);
      onClose();
    } catch (error) {
      console.error("[CK-2623] Error saving the client's daily goals:", {
        errorCode: "CK-2623",
        errorMessage: error?.message,
        clinicId,
        patientId: patient.id,
        timestamp: new Date().toISOString(),
      });
      showError("שמירת היעדים נכשלה, נסו שוב");
    }
  };

  return (
    <div className="ngm-overlay" onClick={onClose} role="presentation">
      <div
        className="ngm-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="הגדרת יעדים יומיים"
      >
        <div className="ngm-header">
          <div className="ngm-title">
            <span className="ngm-title-icon">
              <Target size={20} weight="fill" />
            </span>
            <div className="ngm-title-text">
              <span className="ngm-eyebrow">
                {[patient?.firstName, patient?.lastName].filter(Boolean).join(" ") || "לקוח/ה"}
              </span>
              <h2>יעדים יומיים</h2>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="ngm-body">
          <p className="ngm-note">
            כמה קלוריות, פחמימות, חלבון ושומן הלקוח/ה אמור/ה לצרוך ביום. היעדים מוצגים
            בעמוד האישי מול הדיווחים, ואפשר לערוך אותם בכל רגע מכרטיס הלקוח/ה.
          </p>

          <div className="ngm-row">
            <div className="ngm-field">
              <span className="ngm-label">
                <TrendUp size={14} weight="bold" /> מטרת הליווי
              </span>
              <select value={goalType} onChange={(e) => setGoalType(e.target.value)}>
                {Object.entries(GOAL_TYPES).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="ngm-field">
              <span className="ngm-label">
                <Barbell size={14} weight="bold" /> רמת פעילות
              </span>
              <select value={activityLevel} onChange={(e) => setActivityLevel(e.target.value)}>
                <option value="">לא הוגדר</option>
                {Object.entries(ACTIVITY_LEVEL_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="ngm-field">
            <span className="ngm-label">
              <ChartDonut size={14} weight="bold" /> תוכנית תזונה
            </span>
            <select value={goals.dietPlan || ""} onChange={(e) => applyDietPlan(e.target.value)}>
              <option value="">לא הוגדר</option>
              {DIET_PLANS.map((plan) => (
                <option key={plan.key} value={plan.key}>
                  {plan.label}
                </option>
              ))}
            </select>
            <span className="ngm-hint">ממלאת חלבון / פחמימות / שומן מתוך יעד הקלוריות</span>
          </div>

          <div className="ngm-grid">
            {MACRO_FIELDS.map(({ key, label, unit }) => (
              <div className="ngm-field" key={key}>
                <span className="ngm-label">
                  <MacroIcon macro={key} size={14} />
                  {label} <span className="ngm-unit">({unit})</span>
                </span>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  dir="ltr"
                  lang="en"
                  value={goals[key] ?? ""}
                  onChange={(e) => setGoal(key, e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="—"
                />
                <GoalSuggestion suggestion={suggestions?.[key]} />
              </div>
            ))}
          </div>
        </div>

        <div className="ngm-footer">
          <button type="button" className="ngm-secondary-btn" onClick={onClose} disabled={isLoading}>
            ביטול
          </button>
          <button
            type="button"
            className="ngm-primary-btn"
            onClick={handleSave}
            disabled={!canSave || isLoading}
          >
            {isLoading ? (
              <>
                <LoaderLoop size="xs" />
                שומר...
              </>
            ) : (
              "שמירת היעדים"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NutritionGoalsModal;
